import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RoleSummary } from '@/admin/useAdminRoles';
import { useSoftDeleteRole } from '@/admin/useAdminRoles';
import { RoleAppsPanel } from '@/components/admin/RoleAppsPanel';
import { RoleUsersPanel } from '@/components/admin/RoleUsersPanel';

export interface RolesTableProps {
  roles: RoleSummary[];
}

export function RolesTable({ roles }: RolesTableProps) {
  const { t } = useTranslation();
  const del = useSoftDeleteRole();
  const [expanded, setExpanded] = useState<number | null>(null);

  if (roles.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-white p-6 text-center text-sm text-slate-500">
        {t('roles.table.empty')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border shadow-sm">
      <table className="w-full min-w-[40rem] bg-white text-sm">
      <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
        <tr>
          <th className="px-3 py-2">{t('roles.fields.slug')}</th>
          <th className="px-3 py-2">{t('roles.fields.name')}</th>
          <th className="px-3 py-2 text-center">{t('roles.table.apps')}</th>
          <th className="px-3 py-2 text-center">{t('roles.table.users')}</th>
          <th className="px-3 py-2 text-right">{t('roles.table.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {roles.map((role) => {
          const isDeleted = role.soft_deleted_at !== null;
          return (
            <Fragment key={role.id}>
              <tr className={isDeleted ? 'text-slate-400 line-through' : 'text-slate-900'}>
                <td className="px-3 py-2 font-mono text-xs">{role.slug}</td>
                <td className="px-3 py-2">
                  {role.name}
                  {role.description && (
                    <span className="block text-xs text-slate-500">{role.description}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">{role.app_count}</td>
                <td className="px-3 py-2 text-center">{role.user_count}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => (prev === role.id ? null : role.id))
                    }
                    className="mr-2 text-xs text-appsol-900 hover:underline"
                  >
                    {expanded === role.id
                      ? t('roles.table.hideManage')
                      : t('roles.table.manage')}
                  </button>
                  {!isDeleted && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(t('roles.table.confirmDelete', { name: role.name }))) {
                          del.mutate(role.id);
                        }
                      }}
                      disabled={del.isPending}
                      className="text-xs text-rose-700 hover:underline disabled:opacity-50"
                    >
                      {t('roles.table.delete')}
                    </button>
                  )}
                </td>
              </tr>
              {expanded === role.id && !isDeleted && (
                <tr>
                  <td colSpan={5} className="bg-slate-50 px-3 py-3">
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      <RoleAppsPanel roleId={role.id} />
                      <RoleUsersPanel roleId={role.id} />
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
      </table>
    </div>
  );
}
